import { useEffect, useRef, useState } from 'react';
import {
  CalendarDays,
  Check,
  ChevronDown,
  ChevronRight,
  Clock3,
  Maximize2,
} from 'lucide-react';
import type {
  ManualCampaignForm,
  SelectedScreenMeta,
  StepThreeData,
} from '@/features/campaigns/types/campaign';
import type { RepetitionType } from '@/features/campaigns/utils/startFreshCampaignCreate';
import { FloatingOverlay } from '@/shared/ui/FloatingOverlay';
import { ScheduleModePicker } from './ScheduleModePicker';
import { ScheduleStep } from './ScheduleStep';
import { ScreenWiseScheduleStep } from '@/features/campaigns/components/ScreenWiseScheduleStep';

type ScheduleStepPanelProps = {
  repetitionType: RepetitionType;
  setRepetitionType: (next: RepetitionType) => void;
  hasBookedSlots: boolean;
  onResetSlots: () => void;
  showModePicker: boolean;
  billboardIds: string[];
  campaignData: ManualCampaignForm;
  submitData: StepThreeData;
  setSubmitData: React.Dispatch<React.SetStateAction<StepThreeData>>;
  slotsHoldExpired?: boolean;
  slotsSessionKey: number;
  selectedMeta: Record<string, SelectedScreenMeta>;
  onBackToScreens?: () => void;
  screenSlotsError?: string;
  onClearScreenSlotsError?: () => void;
};

type SectionKey = 'style' | 'hours';

/**
 * Step 3 shell: accordion (like Step 1) + wide overlay workspace for dense hour booking.
 */
export function ScheduleStepPanel({
  repetitionType,
  setRepetitionType,
  hasBookedSlots,
  onResetSlots,
  showModePicker,
  billboardIds,
  campaignData,
  submitData,
  setSubmitData,
  slotsHoldExpired,
  slotsSessionKey,
  selectedMeta,
  onBackToScreens,
  screenSlotsError,
  onClearScreenSlotsError,
}: ScheduleStepPanelProps) {
  const styleRef = useRef<HTMLDivElement>(null);
  const hoursRef = useRef<HTMLDivElement>(null);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
    style: showModePicker,
    hours: true,
  });

  const bookedCount = submitData.bookingSlots.length;
  const screenCount = billboardIds.length;
  const modeLabel =
    repetitionType === 'screen_wise' ? 'Per screen' : 'Same for all';
  const dateLabel =
    campaignData.startDate && campaignData.endDate
      ? `${campaignData.startDate} → ${campaignData.endDate}`
      : 'Campaign dates';

  useEffect(() => {
    if (!showModePicker) {
      setOpenSections((prev) => ({ ...prev, style: false, hours: true }));
    }
  }, [showModePicker]);

  // After hold expiry, reopen workspace so the user can rebook immediately.
  useEffect(() => {
    if (slotsHoldExpired) setWorkspaceOpen(true);
  }, [slotsHoldExpired]);

  const scrollTo = (target: React.RefObject<HTMLDivElement | null>) => {
    requestAnimationFrame(() => {
      window.setTimeout(() => {
        target.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    });
  };

  const toggleSection = (key: SectionKey) => {
    setOpenSections((prev) => {
      const nextOpen = !prev[key];
      const next = { ...prev, [key]: nextOpen };
      if (nextOpen) {
        scrollTo(key === 'style' ? styleRef : hoursRef);
      }
      return next;
    });
  };

  const openWorkspace = () => {
    setOpenSections((prev) => ({ ...prev, hours: true }));
    setWorkspaceOpen(true);
  };

  const closeWorkspace = () => setWorkspaceOpen(false);

  const hoursComplete = bookedCount > 0 && !slotsHoldExpired;

  const workspaceBody =
    repetitionType === 'screen_wise' ? (
      <ScreenWiseScheduleStep
        key={`screenwise-${slotsSessionKey}`}
        embedded
        billboardIds={billboardIds}
        campaignData={campaignData}
        submitData={submitData}
        setSubmitData={setSubmitData}
        slotsHoldExpired={slotsHoldExpired}
        selectedMeta={selectedMeta}
        onBackToScreens={onBackToScreens}
        error={screenSlotsError}
        onClearError={onClearScreenSlotsError}
      />
    ) : (
      <ScheduleStep
        key={slotsSessionKey}
        embedded
        billboardIds={billboardIds}
        campaignData={campaignData}
        submitData={submitData}
        setSubmitData={setSubmitData}
        slotsHoldExpired={slotsHoldExpired}
      />
    );

  return (
    <div className="camp-fade-up flex w-full flex-col gap-3.5">
      <header className="flex items-end justify-between gap-3 text-left">
        <div className="min-w-0">
          <h1 className="m-0 text-xl font-semibold tracking-tight text-[var(--dash-ink)]">
            Schedule &amp; slots
          </h1>
          <p className="mt-0.5 mb-0 text-[13px] text-[var(--dash-muted)]">
            Set booking style, then configure hours in the focused workspace.
          </p>
        </div>
      </header>

      {showModePicker ? (
        <div
          ref={styleRef}
          className={`scroll-mt-4 overflow-hidden rounded-2xl border transition-all duration-300 ${
            openSections.style
              ? 'border-[var(--dash-brand)]/35 bg-white shadow-[0_4px_24px_-8px_rgba(41,121,255,0.12)] ring-1 ring-[var(--dash-brand)]/12'
              : 'border-[#E5EAF0] bg-white hover:border-gray-300'
          }`}
        >
          <button
            type="button"
            onClick={() => toggleSection('style')}
            className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition hover:bg-[#F9FBFC]"
            aria-expanded={openSections.style}
          >
            <div className="flex min-w-0 items-center gap-3">
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ${
                  openSections.style
                    ? 'bg-[var(--dash-brand)] text-white'
                    : 'bg-emerald-500 text-white'
                }`}
              >
                {openSections.style ? '1' : <Check className="h-4 w-4 stroke-[2.5]" />}
              </span>
              <div className="min-w-0">
                <h2 className="m-0 text-sm font-semibold tracking-tight text-[var(--dash-ink)]">
                  Booking style
                </h2>
                <p className="m-0 truncate text-[12px] text-[var(--dash-muted)]">
                  {!openSections.style
                    ? modeLabel
                    : 'Same for all or customize per screen'}
                </p>
              </div>
            </div>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-300 ${
                openSections.style ? 'rotate-180 text-[var(--dash-brand)]' : ''
              }`}
            />
          </button>

          <div
            className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
              openSections.style
                ? 'grid-rows-[1fr] opacity-100'
                : 'pointer-events-none grid-rows-[0fr] opacity-0'
            }`}
          >
            <div className="overflow-hidden">
              <div className="border-t border-black/[0.06] bg-[#F7F8FA]/80 p-3 sm:p-4">
                <ScheduleModePicker
                  value={repetitionType}
                  onChange={(next) => {
                    setRepetitionType(next);
                    setOpenSections((prev) => ({
                      ...prev,
                      style: false,
                      hours: true,
                    }));
                    scrollTo(hoursRef);
                  }}
                  hasBookedSlots={hasBookedSlots}
                  onResetSlots={onResetSlots}
                  visible
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div
        ref={hoursRef}
        className={`scroll-mt-4 overflow-hidden rounded-2xl border transition-all duration-300 ${
          slotsHoldExpired
            ? 'border-amber-300 bg-white shadow-xs'
            : openSections.hours
              ? 'border-[var(--dash-brand)]/35 bg-white shadow-[0_4px_24px_-8px_rgba(41,121,255,0.12)] ring-1 ring-[var(--dash-brand)]/12'
              : 'border-[#E5EAF0] bg-white hover:border-gray-300'
        }`}
      >
        <button
          type="button"
          onClick={() => toggleSection('hours')}
          className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition hover:bg-[#F9FBFC]"
          aria-expanded={openSections.hours}
        >
          <div className="flex min-w-0 items-center gap-3">
            <span
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ${
                slotsHoldExpired
                  ? 'bg-amber-100 text-amber-700'
                  : hoursComplete && !openSections.hours
                    ? 'bg-emerald-500 text-white'
                    : openSections.hours
                      ? 'bg-[var(--dash-brand)] text-white'
                      : 'bg-gray-100 text-gray-500'
              }`}
            >
              {hoursComplete && !openSections.hours && !slotsHoldExpired ? (
                <Check className="h-4 w-4 stroke-[2.5]" />
              ) : showModePicker ? (
                '2'
              ) : (
                '1'
              )}
            </span>
            <div className="min-w-0">
              <h2 className="m-0 text-sm font-semibold tracking-tight text-[var(--dash-ink)]">
                Hours &amp; plays
              </h2>
              <p className="m-0 truncate text-[12px] text-[var(--dash-muted)]">
                {slotsHoldExpired
                  ? 'Reservation expired — reopen workspace to rebook'
                  : hoursComplete && !openSections.hours
                    ? `${bookedCount} slot${bookedCount === 1 ? '' : 's'} · ${modeLabel}`
                    : 'Open the workspace to pick dates, hours, and plays'}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {hoursComplete && !openSections.hours && !slotsHoldExpired ? (
              <span className="hidden items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 sm:inline-flex">
                <Check className="h-3 w-3 stroke-[2.5]" /> Booked
              </span>
            ) : null}
            <ChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${
                openSections.hours ? 'rotate-180 text-[var(--dash-brand)]' : ''
              }`}
            />
          </div>
        </button>

        <div
          className={`grid transition-[grid-template-rows,opacity] duration-300 ease-in-out ${
            openSections.hours
              ? 'grid-rows-[1fr] opacity-100'
              : 'pointer-events-none grid-rows-[0fr] opacity-0'
          }`}
        >
          <div className="overflow-hidden">
            <div className="border-t border-black/[0.06] bg-[#F7F8FA]/80 p-3 sm:p-4">
              <div className="mb-schedule-workspace-card">
                <div className="mb-schedule-workspace-card__meta">
                  <div className="mb-schedule-workspace-card__chips">
                    <span className="mb-schedule-workspace-card__chip">
                      <CalendarDays className="h-3.5 w-3.5" aria-hidden />
                      {dateLabel}
                    </span>
                    <span className="mb-schedule-workspace-card__chip">
                      <Clock3 className="h-3.5 w-3.5" aria-hidden />
                      {bookedCount} slot{bookedCount === 1 ? '' : 's'}
                    </span>
                    <span className="mb-schedule-workspace-card__chip">
                      {screenCount} screen{screenCount === 1 ? '' : 's'} ·{' '}
                      {modeLabel}
                    </span>
                  </div>
                  <p className="mb-schedule-workspace-card__copy">
                    {slotsHoldExpired
                      ? 'Your previous hold expired. Open the workspace and select hours again — campaign basics are still saved.'
                      : bookedCount > 0
                        ? 'Review or adjust booked hours in the full-screen workspace, then continue to payment.'
                        : 'Use the focused workspace for dates and hourly inventory — more room, fewer distractions.'}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={openWorkspace}
                  className="mb-schedule-workspace-card__cta"
                >
                  <Maximize2 className="h-4 w-4" aria-hidden />
                  {bookedCount > 0 || slotsHoldExpired
                    ? 'Open schedule workspace'
                    : 'Configure hours'}
                  <ChevronRight className="h-4 w-4 opacity-80" aria-hidden />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FloatingOverlay
        open={workspaceOpen}
        onClose={closeWorkspace}
        size="xl"
        title="Schedule workspace"
        subtitle={
          repetitionType === 'screen_wise'
            ? 'Book one display at a time — pick a date, then tap hours.'
            : 'Pick a date, tap hours to book, then set plays for all screens.'
        }
        footer={
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="m-0 text-[12px] font-medium text-[var(--dash-muted)]">
              {bookedCount > 0
                ? `${bookedCount} time slot${bookedCount === 1 ? '' : 's'} booked`
                : 'Tap hours to book — then Done'}
            </p>
            <button
              type="button"
              onClick={closeWorkspace}
              className="inline-flex h-10 items-center justify-center rounded-xl bg-[var(--dash-brand)] px-4 text-[13px] font-semibold text-white transition hover:brightness-105"
            >
              Done
            </button>
          </div>
        }
      >
        <div className="p-3 sm:p-4">{workspaceBody}</div>
      </FloatingOverlay>
    </div>
  );
}
